import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './DocumentationViewer.css';

const BASE_URL = process.env.REACT_APP_TAGGING_API_URL || 'https://api.railtech.io';

/* ── Copy button ── */
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="doc-copy-btn"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/* ── Code block ── */
const Code = ({ children }) => (
  <div className="doc-code-wrap">
    <CopyBtn text={children} />
    <pre className="doc-code"><code>{children}</code></pre>
  </div>
);

/* ── Sidebar sections ── */
const SECTIONS = [
  { id: 'quickstart', label: 'Quick Start', group: 'Getting Started' },
  { id: 'auth', label: 'Authentication', group: 'Getting Started' },
  { id: 'mcp', label: 'MCP Setup', group: 'Getting Started' },
  { id: 'save', label: 'Save Memory', group: 'Core API' },
  { id: 'inject', label: 'Retrieve Memories', group: 'Core API' },
  { id: 'feedback', label: 'Send Feedback', group: 'Core API' },
  { id: 'endpoints', label: 'All Endpoints', group: 'Reference' },
  { id: 'errors', label: 'Errors', group: 'Reference' },
];

const DocumentationViewer = () => {
  const [active, setActive] = useState('quickstart');
  const [search, setSearch] = useState('');
  const sectionRefs = useRef({});

  const scrollTo = (id) => {
    setActive(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* Scroll spy */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  /* Filter sections by search */
  const filteredSections = search
    ? SECTIONS.filter((s) => s.label.toLowerCase().includes(search.toLowerCase()))
    : SECTIONS;

  /* Group sidebar items */
  const groups = {};
  filteredSections.forEach((s) => {
    if (!groups[s.group]) groups[s.group] = [];
    groups[s.group].push(s);
  });

  return (
    <>
      <Helmet><title>Documentation - MME</title></Helmet>

      <div className="doc-page">
        {/* Nav */}
        <nav className="doc-nav">
          <div className="doc-nav-inner">
            <Link to="/" className="doc-nav-logo">
              <div className="doc-nav-logo-icon">M</div>
              <span className="doc-nav-logo-text">MME</span>
            </Link>
            <div className="doc-nav-links">
              <Link to="/">Home</Link>
              <a href="#quickstart" className="doc-nav-active">Docs</a>
              <Link to="/signup">Get API Key</Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <div className="doc-hero">
          <div className="doc-hero-inner">
            <h1>API <span>Documentation</span></h1>
            <p>
              Everything you need to integrate MME into your AI agent.
              Three endpoints, one auth header, persistent memory.
            </p>
            <div className="doc-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search documentation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="doc-layout">
          {/* Sidebar */}
          <aside className="doc-sidebar">
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} className="doc-sidebar-section">
                <div className="doc-sidebar-label">{group}</div>
                {items.map((s) => (
                  <button
                    key={s.id}
                    className={`doc-sidebar-link ${active === s.id ? 'active' : ''}`}
                    onClick={() => scrollTo(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ))}
          </aside>

          {/* Content */}
          <main className="doc-content">

            {/* ── QUICK START ── */}
            <section id="quickstart" ref={(el) => (sectionRefs.current.quickstart = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Getting Started</div>
                <h2 className="doc-section-title">Quick start</h2>
                <p className="doc-section-desc">
                  Get from zero to your first saved memory in under 5 minutes.
                </p>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <h3>Base URL</h3>
                <Code>{BASE_URL}</Code>
                <p style={{ marginTop: 12 }}>All API endpoints below are relative to this base URL.</p>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <h3>1. Get your API key</h3>
                <p>
                  Sign up with Google to get your API key. Then exchange it for a JWT token:
                </p>
                <Code>{`curl -X POST ${BASE_URL}/auth/exchange \\
  -H "Content-Type: application/json" \\
  -d '{"apiKey": "mme_live_YOUR_API_KEY"}'`}</Code>
                <p style={{ marginTop: 12 }}>
                  The response contains a <code className="doc-inline-code">token</code> field — use it as your Bearer token for all API calls.
                </p>
                <Link to="/signup" style={{ color: '#6C5CE7', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
                  Get your API key &rarr;
                </Link>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #00B894' }}>
                <h3>2. Save your first memory</h3>
                <Code>{`curl -X POST ${BASE_URL}/memory/save \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "User prefers Python and works in fintech"}'`}</Code>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #F39C12' }}>
                <h3>3. Retrieve relevant memories</h3>
                <Code>{`curl -X POST ${BASE_URL}/memory/inject \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt": "what programming language should I use?", "tokenBudget": 2048}'`}</Code>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #1A1A2E' }}>
                <h3>4. Send feedback</h3>
                <p>Tell MME whether the retrieved memories were useful. This trains the system to surface better results over time.</p>
                <Code>{`curl -X POST ${BASE_URL}/memory/feedback \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{"packId": "PACK_ID", "accepted": true}'`}</Code>
              </div>
            </section>

            {/* ── AUTHENTICATION ── */}
            <section id="auth" ref={(el) => (sectionRefs.current.auth = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Getting Started</div>
                <h2 className="doc-section-title">Authentication</h2>
                <p className="doc-section-desc">
                  All protected endpoints require a JWT Bearer token.
                </p>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <h3>API key exchange</h3>
                <p>Exchange your API key for a JWT token:</p>
                <Code>{`curl -X POST ${BASE_URL}/auth/exchange \\
  -H "Content-Type: application/json" \\
  -d '{"apiKey": "mme_live_YOUR_API_KEY"}'`}</Code>
                <h3 style={{ marginTop: 20 }}>Response</h3>
                <Code>{`{
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "userId": "user_abc123",
  "orgId": "org_def456",
  "email": "you@example.com"
}`}</Code>
                <div className="doc-note" style={{ marginTop: 16 }}>
                  Tokens expire after 7 days. Exchange your API key again to get a fresh token.
                </div>
              </div>

              <div className="doc-card">
                <h3>Authorization header</h3>
                <p>Include the JWT in every request:</p>
                <Code>{`Authorization: Bearer <your-jwt-token>`}</Code>

                <div className="doc-note" style={{ marginTop: 16 }}>
                  The JWT is signed with HMAC (HS256). Tokens carry your <code className="doc-inline-code">user_id</code>, <code className="doc-inline-code">org_id</code>, and optional <code className="doc-inline-code">roles</code> claims.
                </div>
              </div>

              <div className="doc-card">
                <h3>JWT claims</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Claim</th><th>Type</th><th>Notes</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>user_id</code></td><td>string</td><td>Primary user identifier</td></tr>
                    <tr><td><code>org_id</code></td><td>string</td><td>Required for tenant isolation</td></tr>
                    <tr><td><code>project_id</code></td><td>string</td><td>Optional project scope</td></tr>
                    <tr><td><code>roles</code></td><td>string[]</td><td>e.g. <code>["user"]</code> or <code>["admin"]</code></td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── MCP SETUP ── */}
            <section id="mcp" ref={(el) => (sectionRefs.current.mcp = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Getting Started</div>
                <h2 className="doc-section-title">MCP Setup</h2>
                <p className="doc-section-desc">
                  Connect any MCP-compatible client to MME using the Model Context Protocol.
                </p>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <h3>Claude Desktop</h3>
                <p>
                  Add this to your <code className="doc-inline-code">claude_desktop_config.json</code>:
                </p>
                <Code>{`{
  "mcpServers": {
    "mme-memory": {
      "url": "${BASE_URL}/mcp/http",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`}</Code>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #00B894' }}>
                <h3>Claude Code</h3>
                <p>
                  Add to <code className="doc-inline-code">.claude/settings.json</code> or run <code className="doc-inline-code">claude mcp add</code>:
                </p>
                <Code>{`{
  "mcpServers": {
    "mme-memory": {
      "url": "${BASE_URL}/mcp/http",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`}</Code>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #F39C12' }}>
                <h3>Cursor / Continue.dev / Windsurf</h3>
                <p>
                  Any MCP-compatible client works. Point your MCP config at:
                </p>
                <Code>{`${BASE_URL}/mcp/http`}</Code>
                <p style={{ marginTop: 12 }}>
                  With header: <code className="doc-inline-code">Authorization: Bearer YOUR_API_KEY</code> (your raw <code className="doc-inline-code">mme_live_…</code> key — no JWT exchange needed for MCP).
                </p>
              </div>

              <div className="doc-card">
                <h3>Available MCP tools</h3>
                <p>Once connected, your agent gets three tools:</p>
                <table className="doc-table">
                  <thead>
                    <tr><th>Tool</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>mme_save</code></td><td>Save a memory block. Params: <code>content</code> (required), <code>section</code>, <code>status</code>, <code>source</code></td></tr>
                    <tr><td><code>mme_inject</code></td><td>Retrieve token-budgeted memories. Params: <code>prompt</code> (required), <code>token_budget</code>, <code>limit</code></td></tr>
                    <tr><td><code>mme_feedback</code></td><td>Record feedback on a pack. Params: <code>pack_id</code>, <code>accepted</code> (required), <code>tags</code>, <code>item_ids</code></td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── SAVE MEMORY ── */}
            <section id="save" ref={(el) => (sectionRefs.current.save = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Core API</div>
                <h2 className="doc-section-title">Save Memory</h2>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <p style={{ marginBottom: 16 }}>
                  <span className="doc-method doc-method-post">POST</span>
                  <span className="doc-endpoint-path">/memory/save</span>
                </p>
                <p>Store a memory block. MME auto-tags the content using the tagmaker service.</p>

                <h3 style={{ marginTop: 20 }}>Request body</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>content</code></td><td>string</td><td>Yes</td><td>The text to remember</td></tr>
                    <tr><td><code>section</code></td><td>string</td><td>No</td><td>Category (e.g. "work", "personal")</td></tr>
                    <tr><td><code>status</code></td><td>string</td><td>No</td><td>"draft", "completed", etc.</td></tr>
                    <tr><td><code>source</code></td><td>string</td><td>No</td><td>Source identifier</td></tr>
                    <tr><td><code>tags</code></td><td>string[] or Tag[]</td><td>No</td><td>Manual tags (auto-tagged if omitted)</td></tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: 20 }}>Response (201)</h3>
                <Code>{`{
  "id": "665f1a2b3c4d5e6f7a8b9c0d",
  "message": "Memory saved",
  "userId": "user_123",
  "orgId": "org_456",
  "tagsFlat": ["python", "fintech", "preferences"],
  "status": "",
  "success": true
}`}</Code>

                <div className="doc-note" style={{ marginTop: 16 }}>
                  If a duplicate memory is detected, the response returns <code className="doc-inline-code">"duplicate": true</code> with status 200 instead of 201.
                </div>
              </div>
            </section>

            {/* ── RETRIEVE MEMORIES ── */}
            <section id="inject" ref={(el) => (sectionRefs.current.inject = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Core API</div>
                <h2 className="doc-section-title">Retrieve Memories</h2>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #00B894' }}>
                <p style={{ marginBottom: 16 }}>
                  <span className="doc-method doc-method-post">POST</span>
                  <span className="doc-endpoint-path">/memory/inject</span>
                </p>
                <p>Retrieve a token-budgeted pack of relevant memories for a given prompt. Memories are scored by tag activation, recency decay, and feedback history.</p>

                <h3 style={{ marginTop: 20 }}>Request body</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>prompt</code></td><td>string</td><td>Yes</td><td>Natural-language query</td></tr>
                    <tr><td><code>tokenBudget</code></td><td>int</td><td>No</td><td>Max tokens in the pack (default: 2048)</td></tr>
                    <tr><td><code>limit</code></td><td>int</td><td>No</td><td>Max candidates to score (default: 20)</td></tr>
                    <tr><td><code>filters</code></td><td>object</td><td>No</td><td>Filter by section, status, or date</td></tr>
                    <tr><td><code>debug</code></td><td>bool</td><td>No</td><td>Include scoring details</td></tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: 20 }}>Response (200)</h3>
                <Code>{`{
  "packId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "seedTags": ["python", "programming"],
  "tokenBudget": 2048,
  "totalTokens": 342,
  "items": [
    {
      "id": "665f1a2b3c4d5e6f7a8b9c0d",
      "tags": ["python", "fintech"],
      "excerpt": "User prefers Python and works in fintech",
      "tokenCost": 45,
      "score": {
        "activation": 0.9,
        "recency": 0.7,
        "total": 0.84
      }
    }
  ]
}`}</Code>

                <div className="doc-note" style={{ marginTop: 16 }}>
                  Save the <code className="doc-inline-code">packId</code>, <code className="doc-inline-code">items[].id</code>, and <code className="doc-inline-code">seedTags</code> — you'll need them for the feedback call.
                </div>
              </div>
            </section>

            {/* ── SEND FEEDBACK ── */}
            <section id="feedback" ref={(el) => (sectionRefs.current.feedback = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Core API</div>
                <h2 className="doc-section-title">Send Feedback</h2>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #F39C12' }}>
                <p style={{ marginBottom: 16 }}>
                  <span className="doc-method doc-method-post">POST</span>
                  <span className="doc-endpoint-path">/memory/feedback</span>
                </p>
                <p>Tell MME whether a memory pack was useful. Positive feedback promotes those memories for future retrieval; negative feedback lets them decay.</p>

                <h3 style={{ marginTop: 20 }}>Request body</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>packId</code></td><td>string</td><td>Yes</td><td>Pack ID from /memory/inject</td></tr>
                    <tr><td><code>accepted</code></td><td>bool</td><td>Yes</td><td>true = useful, false = not useful</td></tr>
                    <tr><td><code>tags</code></td><td>string[]</td><td>No</td><td>Tags from the pack (improves learning)</td></tr>
                    <tr><td><code>itemIds</code></td><td>string[]</td><td>No</td><td>Item IDs from the pack (improves learning)</td></tr>
                  </tbody>
                </table>

                <h3 style={{ marginTop: 20 }}>Response (200)</h3>
                <Code>{`{
  "status": "success",
  "event": {
    "orgId": "org_456",
    "packId": "a1b2c3d4-...",
    "accepted": true,
    "tags": ["python"],
    "itemIds": ["665f1a2b..."]
  }
}`}</Code>
              </div>
            </section>

            {/* ── ALL ENDPOINTS ── */}
            <section id="endpoints" ref={(el) => (sectionRefs.current.endpoints = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Reference</div>
                <h2 className="doc-section-title">All Endpoints</h2>
                <p className="doc-section-desc">
                  Complete endpoint reference.
                </p>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #6C5CE7' }}>
                <h3>Memory &amp; Tags</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Method</th><th>Path</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/memory/save</code></td><td>Save a memory block</td></tr>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/memory/inject</code></td><td>Retrieve token-budgeted memories</td></tr>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/memory/feedback</code></td><td>Send pack feedback</td></tr>
                    <tr><td><span className="doc-method doc-method-get">GET</span></td><td><code>/memory/query</code></td><td>Query memories with filters</td></tr>
                    <tr><td><span className="doc-method doc-method-get">GET</span></td><td><code>/memory/recent</code></td><td>Get recent memories</td></tr>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/memory/promote</code></td><td>Promote memory importance</td></tr>
                    <tr><td><span className="doc-method doc-method-delete">DEL</span></td><td><code>/memory/:id</code></td><td>Delete a memory</td></tr>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/search/semantic</code></td><td>Semantic search across memories</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #00B894' }}>
                <h3>MCP &amp; Auth</h3>
                <table className="doc-table">
                  <thead>
                    <tr><th>Method</th><th>Path</th><th>Description</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><span className="doc-method doc-method-get">GET</span></td><td><code>/mcp/sse</code></td><td>MCP SSE endpoint (for agents)</td></tr>
                    <tr><td><span className="doc-method doc-method-post">POST</span></td><td><code>/auth/exchange</code></td><td>Exchange API key for JWT</td></tr>
                    <tr><td><span className="doc-method doc-method-get">GET</span></td><td><code>/health</code></td><td>Health check (no auth)</td></tr>
                    <tr><td><span className="doc-method doc-method-get">GET</span></td><td><code>/version</code></td><td>Version info (no auth)</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── ERRORS ── */}
            <section id="errors" ref={(el) => (sectionRefs.current.errors = el)} className="doc-section">
              <div className="doc-section-header">
                <div className="doc-section-label">Reference</div>
                <h2 className="doc-section-title">Error Responses</h2>
              </div>

              <div className="doc-card" style={{ borderTop: '2px solid #1A1A2E' }}>
                <table className="doc-table">
                  <thead>
                    <tr><th>Status</th><th>Meaning</th><th>Common causes</th></tr>
                  </thead>
                  <tbody>
                    <tr><td><code>400</code></td><td>Bad Request</td><td>Missing required fields, empty content, invalid JSON</td></tr>
                    <tr><td><code>401</code></td><td>Unauthorized</td><td>Missing or invalid JWT token</td></tr>
                    <tr><td><code>403</code></td><td>Forbidden</td><td>org_id mismatch between JWT and request body</td></tr>
                    <tr><td><code>429</code></td><td>Rate Limited</td><td>Too many requests — retry after the indicated delay</td></tr>
                    <tr><td><code>500</code></td><td>Server Error</td><td>Internal error — contact support</td></tr>
                  </tbody>
                </table>
                <p style={{ marginTop: 16 }}>All error responses return JSON:</p>
                <Code>{`{
  "error": "description of the problem"
}`}</Code>
              </div>
            </section>

          </main>
        </div>

        {/* Footer */}
        <footer style={{ borderTop: '1px solid #E8E8F0', padding: '48px 32px', background: '#FDFCFA' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: '#6C5CE7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 12 }}>M</div>
              <span style={{ fontWeight: 600, color: '#64648C', fontSize: 14 }}>&copy; 2026 Railtech Inc.</span>
            </div>
            <div style={{ display: 'flex', gap: 28 }}>
              <Link to="/" style={{ fontSize: 14, color: '#9494B8', textDecoration: 'none', fontWeight: 500 }}>Home</Link>
              <a href="mailto:info@railtech.io" style={{ fontSize: 14, color: '#9494B8', textDecoration: 'none', fontWeight: 500 }}>Contact</a>
              <Link to="/login" style={{ fontSize: 14, color: '#9494B8', textDecoration: 'none', fontWeight: 500 }}>Sign in</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DocumentationViewer;
